import { Ability } from '@casl/ability'
import { abilitiesPlugin } from '@casl/vue'
import { parse, serialize } from 'cookie-es'
import { destr } from 'destr'
import { clearToken } from '@/utils/token'

export const USER_ABILITY_RULES_COOKIE = 'userAbilityRules'
export const APP_BUILD_VERSION_STORAGE_KEY = 'appBuildVersion'


/** CASL rules that allow all actions on all subjects (full access). */
export const FULL_ACCESS_RULES = [{ action: 'manage', subject: 'all' }]

/**
 * Explicit default rules when API has no ability concept. Set only on successful login,
 * never as a fallback when the ability cookie is missing (guard must redirect to login instead).
 */
export const DEFAULT_LOGGED_IN_RULES = FULL_ACCESS_RULES

/**
 * Singleton ability instance. Deny by default when no rules are set.
 * Updated from userAbilityRules cookie (e.g. set by backend via login response).
 */
export const ability = new Ability([])

/**
 * Parse ability rules from document.cookie (for use in router guard outside Vue context).
 * @returns {Array} CASL rules, e.g. [{ action: 'read', subject: 'Account' }, ...]
 */
export function getAbilityRulesFromCookie() {
  const cookies = parse(document.cookie)
  const raw = cookies[USER_ABILITY_RULES_COOKIE]
  if (raw == null || raw === '')
    return []
  try {
    const value = destr(decodeURIComponent(raw))
    return Array.isArray(value) ? value : []
  }
  catch {
    return []
  }
}

/**
 * Update the shared ability instance from userAbilityRules cookie.
 * Call this before checking ability in router guard so rules are current.
 */
export function updateAbilityFromCookie() {
  const rules = getAbilityRulesFromCookie()
  ability.update(rules)
}

/**
 * Grant full access and persist to cookie. Do NOT use when rules are missing in the guard
 * (that would allow bypass by clearing cookies). Only for explicit flows (e.g. dev).
 */
export function setFullAccessForLoggedInUser() {
  if (typeof document === 'undefined') return
  document.cookie = serialize(USER_ABILITY_RULES_COOKIE, JSON.stringify(FULL_ACCESS_RULES), {
    path: '/',
    maxAge: 60 * 60 * 24 * 30,
  })
  ability.update(FULL_ACCESS_RULES)
}

/**
 * Clear ability rules cookie (e.g. on logout or token clear).
 */
export function clearAbilityRulesCookie() {
  if (typeof document === 'undefined') return
  document.cookie = serialize(USER_ABILITY_RULES_COOKIE, '', { path: '/', maxAge: -1 })
  ability.update([])
}

// Clear stale auth/ability cache when a new deployment is detected.
export function invalidateAuthCacheOnBuildChange() {
  if (typeof window === 'undefined' || typeof localStorage === 'undefined') return false

  const currentBuildVersion = import.meta.env.VITE_APP_BUILD_VERSION
  if (!currentBuildVersion) return false

  const previousBuildVersion = localStorage.getItem(APP_BUILD_VERSION_STORAGE_KEY)
  if (previousBuildVersion === currentBuildVersion) return false

  localStorage.setItem(APP_BUILD_VERSION_STORAGE_KEY, currentBuildVersion)

  // First app load: persist build version only, avoid forcing logout.
  if (previousBuildVersion == null) return false

  clearAbilityRulesCookie()
  clearToken()
  document.cookie = serialize('accessToken', '', { path: '/', maxAge: -1 })

  return true
}

export default function (app) {
  updateAbilityFromCookie()
  app.use(abilitiesPlugin, ability, { useGlobalProperties: true })
}
